import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { DialogClose } from '@qualifio/ui-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfCodeArea } from '@qualifio/ui-commons/dist/components/qlf-code-area';
import { RuleAttributes } from '@loyalty/shared-common/rule-api-client';
import { faCopy } from '@fortawesome/pro-solid-svg-icons/faCopy';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { Title } from '../../../shared/layout/Title';
import { ruleAttributesListToRuleInputs } from './rule-import-export-utils';

export interface RuleExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  rules: RuleAttributes[];
}

export function RuleExportModal({
  isOpen,
  onClose,
  rules,
}: RuleExportModalProps): React.ReactElement {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const [jsonContent, setJsonContent] = useState('');
  const [copyError, setCopyError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      const ruleInputs = ruleAttributesListToRuleInputs(rules);
      setJsonContent(JSON.stringify(ruleInputs, null, 2));
      setCopyError(null);
    }
  }, [isOpen, rules]);

  const handleCopy = useCallback(async () => {
    try {
      setCopyError(null);
      await navigator.clipboard.writeText(jsonContent);
      notify(t('rules.import-export.export.copied'), 'success');
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setCopyError(message);
    }
  }, [jsonContent, notify, onClose, t]);

  const handleClose = useCallback(() => {
    setCopyError(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogClose close={handleClose} />
      <DialogTitle>
        <Title>{t('rules.import-export.export.title')}</Title>
      </DialogTitle>
      <DialogContent>
        {copyError && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {copyError}
          </Alert>
        )}
        <QlfCodeArea
          id="rules-export-json-editor"
          value={jsonContent}
          defaultLanguage="json"
          allowFullScreen
          height="70vh"
          onChange={value => setJsonContent(value || '')}
        />
      </DialogContent>
      <DialogActions sx={{ paddingX: '40px', justifyContent: 'space-between !important' }}>
        <Button color="grey" onClick={handleClose} startIcon={<FontAwesomeIcon icon={faXmark} />}>
          {t('rules.import-export.export.cancel')}
        </Button>
        <Button onClick={handleCopy} startIcon={<FontAwesomeIcon icon={faCopy} />} autoFocus>
          {t('rules.import-export.export.copy')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
